import React, { useEffect, useState } from 'react';
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup
} from "react-simple-maps";
import * as d3Scale from "d3-scale";
import { interpolateRgb } from "d3-interpolate";
import { Tooltip } from "@mui/material";
import Colors from "./ui/Colors";
// Import county data
import countyData from "../data/countyData.json";

// URL for county GeoJSON (counties-10m.json from the same CDN)
const geoUrl = "https://cdn.jsdelivr.net/npm/us-atlas@3/counties-10m.json";

// Lookup table for state abbreviations to full names
const stateAbbreviations = {
  "AL": "Alabama", "AK": "Alaska", "AZ": "Arizona", "AR": "Arkansas",
  "CA": "California", "CO": "Colorado", "CT": "Connecticut", "DE": "Delaware",
  "FL": "Florida", "GA": "Georgia", "HI": "Hawaii", "ID": "Idaho",
  "IL": "Illinois", "IN": "Indiana", "IA": "Iowa", "KS": "Kansas",
  "KY": "Kentucky", "LA": "Louisiana", "ME": "Maine", "MD": "Maryland",
  "MA": "Massachusetts", "MI": "Michigan", "MN": "Minnesota", "MS": "Mississippi",
  "MO": "Missouri", "MT": "Montana", "NE": "Nebraska", "NV": "Nevada",
  "NH": "New Hampshire", "NJ": "New Jersey", "NM": "New Mexico", "NY": "New York",
  "NC": "North Carolina", "ND": "North Dakota", "OH": "Ohio", "OK": "Oklahoma",
  "OR": "Oregon", "PA": "Pennsylvania", "RI": "Rhode Island", "SC": "South Carolina",
  "SD": "South Dakota", "TN": "Tennessee", "TX": "Texas", "UT": "Utah",
  "VT": "Vermont", "VA": "Virginia", "WA": "Washington", "WV": "West Virginia",
  "WI": "Wisconsin", "WY": "Wyoming", "DC": "District of Columbia"
};

// Reverse lookup from full name to abbreviation
const stateFullNameToAbbreviation = Object.entries(stateAbbreviations)
  .reduce((acc, [abbr, fullName]) => {
    acc[fullName] = abbr;
    return acc;
  }, {});

// County FIPS to name mapping
const stateFipsToAbbr = {
  "01": "AL", "02": "AK", "04": "AZ", "05": "AR", "06": "CA", "08": "CO", "09": "CT",
  "10": "DE", "11": "DC", "12": "FL", "13": "GA", "15": "HI", "16": "ID", "17": "IL",
  "18": "IN", "19": "IA", "20": "KS", "21": "KY", "22": "LA", "23": "ME", "24": "MD",
  "25": "MA", "26": "MI", "27": "MN", "28": "MS", "29": "MO", "30": "MT", "31": "NE",
  "32": "NV", "33": "NH", "34": "NJ", "35": "NM", "36": "NY", "37": "NC", "38": "ND",
  "39": "OH", "40": "OK", "41": "OR", "42": "PA", "44": "RI", "45": "SC", "46": "SD",
  "47": "TN", "48": "TX", "49": "UT", "50": "VT", "51": "VA", "53": "WA", "54": "WV",
  "55": "WI", "56": "WY"
};

const CountyMap = ({ selectedYear, selectedState, selectedCounty, onCountySelect }) => {
  const [processedData, setProcessedData] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  
  // Memoized values to avoid recreating color scales and calculation functions on every render
  const [colorScaleParams, setColorScaleParams] = useState({
    minPop: 0,
    maxPop: 1
  });

  useEffect(() => {
    // Process county data for the selected year
    if (!selectedYear) {
      console.warn("No year selected for county data");
      return;
    }
    
    const year = selectedYear.toString();
    const processed = [];
    
    try {
      // Verify we have the countyData properly loaded
      console.log("County data structure:", 
        Object.keys(countyData), 
        countyData.states ? Object.keys(countyData.states).length : 'No states found'
      );

      // Go through all states and their counties
      Object.entries(countyData.states || {}).forEach(([stateName, counties]) => {
        // Skip if a specific state is selected and this isn't it
        const stateAbbr = stateFullNameToAbbreviation[stateName];
        
        if (selectedState !== "all" && stateAbbr !== selectedState) {
          return;
        }
        
        counties.forEach(county => {
          try {
            // Get population value as original string
            const populationStr = county.population[year] || "0";
                        
            // Replace all periods with empty strings first, then parse as an integer
            const population = parseInt(populationStr.replace(/\./g, ''), 10);
            
            processed.push({
              countyName: county.countyName,
              stateName: stateName,
              stateCode: stateAbbr,
              population: population,
              rawPopulation: populationStr // Keep raw value for reference
            });
          } catch (err) {
            console.error("Error processing county:", county, err);
          }
        });
      });
      
      console.log(`Processed ${processed.length} counties for year ${year} and state ${selectedState}`);
      console.log("Sample counties:", processed.slice(0, 3));
      
      // Calculate min/max once when data changes
      const values = processed.map(d => d.population).filter(p => p > 0);
      const minPop = values.length > 0 ? Math.min(...values) : 0;
      const maxPop = values.length > 0 ? Math.max(...values) : 1;
      
      setColorScaleParams({ minPop, maxPop });
      setProcessedData(processed);
    } catch (err) {
      console.error("Error processing county data:", err);
    }
  }, [selectedYear, selectedState]);

  // Window resize handling
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Find min and max population for color scale
  const values = processedData.map(d => d.population).filter(p => p > 0);
  const minPop = values.length > 0 ? Math.min(...values) : 0;
  const maxPop = values.length > 0 ? Math.max(...values) : 1;
  
  console.log("County population range:", minPop, "to", maxPop);
  
  // Define custom cold-to-hot color scale
  const coldToHotColors = [
    "#7cd7ec", // Light blue (coldest)
    "#1986a2", // Darker blue
    "#cdc2a2", // Beige/tan (transition)
    "#f7f7b3", // Pale yellow
    "#f2ab4b", // Orange
    "#da3a27", // Red-orange
    "#bb312d"  // Red (hottest)
  ];
  
  // Create a custom color interpolator with custom domain
  const customColorScale = d3Scale.scaleLinear()
    .domain([0, 0.07, 0.17, 0.20, 0.25, 0.30, 0.35]) // Custom domain values
    .range(coldToHotColors)
    .interpolate(interpolateRgb); // Use RGB interpolation for smoother transitions
  
  // Create a power scale for population values
  const populationScale = d3Scale.scalePow()
    .exponent(0.3) // Increased from 0.3 to provide better contrast across population ranges
    .domain([minPop, maxPop])
    .range([0, 1]);
  
  // Custom color interpolator function
  const colorInterpolator = (value) => {
    return customColorScale(value);
  };

  // Format function to display with thousand separators
  const formatPopulation = (value) => {
    return value.toLocaleString();
  };

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      <ComposableMap
        projection="geoAlbersUsa"
        projectionConfig={{ scale: 800 }}
        height={500}
        width={800}
      >
        <ZoomableGroup 
          zoom={1}
        >
          <Geographies geography={geoUrl}>
            {({ geographies }) => (
              geographies && geographies.map(geo => {
                // Counties have GEOID format SSCCC where SS is state code and CCC is county code
                const geoId = geo.id;
                if (!geoId) return null;
                
                const stateCode = geoId.substring(0, 2);
                
                // Only show counties for the selected state or all counties if none selected
                if (selectedState !== "all" && stateCode !== selectedState) {
                  return null;
                }
                
                // Convert FIPS state code to abbreviation
                const stateAbbr = stateFipsToAbbr[stateCode];
                const stateName = stateAbbreviations[stateAbbr];
                
                // Get county name from properties
                const countyName = geo.properties.name;
                
                // Improved county matching logic
                const countyObj = processedData.find(county => {
                  const countyNameNoSuffix = county.countyName.replace(/ County$/, "");
                  return county.stateCode === stateAbbr && 
                    (countyName === countyNameNoSuffix || 
                     countyName.includes(countyNameNoSuffix) ||
                     countyNameNoSuffix.includes(countyName));
                });
                
                // Extract population from matching county
                const population = countyObj?.population || 0;
                
                // Determine if this is the selected county
                const isSelected = selectedCounty === geoId;
                
                // Color based on population or selection
                const normalizedValue = population > 0 ? populationScale(population) : 0;
                const color = isSelected 
                  ? Colors.third  
                  : (population > 0 
                      ? colorInterpolator(normalizedValue) 
                      : "#f0f0f0");  // Light gray for counties with no data
                
                return (
                  <Tooltip
                    key={geo.rsmKey}
                    title={
                      <>
                        <strong>{countyName} County, {stateName}</strong>
                        <div>Population: {population > 0 ? formatPopulation(population) : "No data"}</div>
                        {countyObj && <div>Source: {countyObj.rawPopulation}</div>}
                      </>
                    }
                    arrow
                  >
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={color}
                      stroke="#FFF"
                      strokeWidth={0.25} // Thinner for counties
                      style={{
                        default: { outline: "none" },
                        hover: {
                          fill: Colors.third,
                          cursor: "pointer",
                          outline: "none"
                        },
                        pressed: { outline: "none" }
                      }}
                      onClick={() => onCountySelect(geoId)}
                    />
                  </Tooltip>
                );
              })
            )}
          </Geographies>
          
          {/* Selected County Highlight */}
          {selectedCounty && (
            <Geographies geography={geoUrl}>
              {({ geographies }) => geographies
                .filter(geo => geo.id === selectedCounty)
                .map(geo => (
                  <Geography
                    key={geo.rsmKey + "-highlight"}
                    geography={geo}
                    fill="none"
                    stroke={Colors.primary}
                    strokeWidth={2}
                    style={{
                      default: { outline: "none" },
                      hover: { outline: "none" }
                    }}
                  />
                ))}
            </Geographies>
          )}
        </ZoomableGroup>
      </ComposableMap>

      {/* Legend */}
      <div style={{
        position: 'absolute',
        ...(windowWidth < 768 
          ? { bottom: -40, right: 10 }
          : { bottom: 10, left: 20 }),
        backgroundColor: 'white',
        padding: windowWidth < 768 ? '8px' : '12px',
        borderRadius: '4px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
        fontSize: windowWidth < 768 ? '90%' : '100%',
        maxWidth: windowWidth < 768 ? '180px' : '220px',
        zIndex: 1000
      }}>
        <div style={{ 
          marginBottom: windowWidth < 768 ? '4px' : '8px', 
          fontWeight: 'bold', 
          fontSize: windowWidth < 768 ? '12px' : '14px' 
        }}>
          Population by County
        </div>
        
        {/* Color gradient with labels - updated to match custom domain */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: windowWidth < 768 ? '2px' : '4px' }}>
          {[0.35, 0.30, 0.25, 0.20, 0.17, 0.07, 0].map((t, i) => {
            // Calculate population values that correspond to each domain point
            let popValue = Math.round((t / 0.35) * maxPop);
            
            return (
              <div key={i} style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                  width: windowWidth < 768 ? '15px' : '20px',
                  height: windowWidth < 768 ? '15px' : '20px',
                  backgroundColor: colorInterpolator(t),
                  marginRight: windowWidth < 768 ? '4px' : '8px',
                  border: '1px solid #ddd'
                }} />
                <span style={{ fontSize: windowWidth < 768 ? '11px' : '13px' }}>
                  {i === 0 ? `${formatPopulation(popValue)}+` : 
                   i === 6 ? `< ${formatPopulation(Math.round(maxPop * 0.07 / 0.35))}` :
                   `${formatPopulation(popValue)}`}
                </span>
              </div>
            );
          })}
        </div>
        
        {windowWidth >= 768 && (
          <div style={{ fontSize: '11px', color: '#666', marginTop: '8px', fontStyle: 'italic' }}>
            {selectedYear} population data
          </div>
        )}
      </div>
    </div>
  );
};

export default CountyMap; 